import axios from "axios";
import React, { useEffect, useState } from "react";
import { Button } from "../../components/ui/button";
import TransferModal from "../../components/modals/TransferModal";
import PageSizeSelect from "../../components/ui/PageSizeSelect";
import { toast } from "react-toastify";
import { LoaderPinwheel, Search, X, ArrowRight } from "lucide-react";

const ESTATUS_STYLES = {
  Completada: "bg-green-50 text-green-700 border border-green-200",
  Pendiente: "bg-yellow-50 text-yellow-700 border border-yellow-200",
};

const TransfersPage = () => {
  const [transferencias, setTransferencias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [almacenes, setAlmacenes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [itemsPerPage, setItemsPerPage] = useState(10);
  const token = localStorage.getItem("token");
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8001/api";

  const fetchTransferencias = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/transferencias`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTransferencias(res.data);
    } catch {
      toast.error("Error al cargar transferencias");
    } finally {
      setLoading(false);
    }
  };

  const fetchAlmacenes = async () => {
    const res = await axios.get(`${API_URL}/almacenes`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setAlmacenes(res.data);
  };

  useEffect(() => {
    fetchTransferencias();
    fetchAlmacenes();
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, transferencias]);

  const filteredTransferencias = transferencias.filter((t) => {
    const term = searchTerm.toLowerCase();
    return [
      t.almacenOrigen?.nombre,
      t.almacenDestino?.nombre,
      t.estatus,
      t.items?.length,
    ].some((v) =>
      String(v ?? "")
        .toLowerCase()
        .includes(term),
    );
  });

  const totalPages = Math.max(
    1,
    Math.ceil(filteredTransferencias.length / itemsPerPage),
  );
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTransferencias = filteredTransferencias.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">
            Transferencias
          </h2>
          <p className="text-sm text-gray-500">
            {transferencias.length} transferencia
            {transferencias.length !== 1 ? "s" : ""}
          </p>
        </div>
        <Button
          onClick={() => setShowModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          + Nueva transferencia
        </Button>
      </div>

      {loading && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-700">
          <LoaderPinwheel className="h-4 w-4 animate-spin text-blue-600" />
          Cargando datos, por favor espera...
        </div>
      )}

      {/* Buscador */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Buscar por almacén origen, destino o estado…"
          className="w-full sm:w-96 pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none"
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Tabla */}
      <div className="flex items-center justify-end mb-2">
        <PageSizeSelect
          value={itemsPerPage}
          onChange={(size) => {
            setItemsPerPage(size);
            setCurrentPage(1);
          }}
        />
      </div>
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-100 text-gray-600 text-xs uppercase tracking-wide">
              <th className="px-4 py-3 text-left">Fecha</th>
              <th className="px-4 py-3 text-left">Movimiento</th>
              <th className="px-4 py-3 text-center">Ítems</th>
              <th className="px-4 py-3 text-left">Estatus</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {paginatedTransferencias.length > 0 ? (
              paginatedTransferencias.map((t) => (
                <tr key={t._id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-gray-500">
                    {new Date(t.fecha).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 text-gray-700">
                      <span className="font-medium">
                        {t.almacenOrigen?.nombre || "—"}
                      </span>
                      <ArrowRight className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                      <span className="font-medium">
                        {t.almacenDestino?.nombre || "—"}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-block px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold">
                      {t.items?.length ?? 0}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold ${ESTATUS_STYLES[t.estatus] || "bg-gray-100 text-gray-600"}`}
                    >
                      {t.estatus}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="py-12 text-center">
                  <Search className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">
                    No hay transferencias para la búsqueda actual
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
        <span className="text-xs text-gray-400">
          {filteredTransferencias.length === 0
            ? "0"
            : `${startIndex + 1}–${Math.min(startIndex + itemsPerPage, filteredTransferencias.length)}`}{" "}
          de {filteredTransferencias.length}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            ← Anterior
          </Button>
          <span className="px-2 py-1 bg-gray-100 rounded-md text-xs">
            {currentPage} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Siguiente →
          </Button>
        </div>
      </div>

      <TransferModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onSuccess={fetchTransferencias}
        almacenes={almacenes}
        token={token}
        API_URL={API_URL}
      />
    </div>
  );
};

export default TransfersPage;
